// Hand-rolled axis primitives for editorial charts.
//
// Why not @visx/axis: visx's <Axis> components ship with default tick marks,
// axis lines, and a configurable tick formatter we don't need. We strip all
// of that anyway — just labels in monospace, no tick marks, no axis line.
// 8-10 lines of JSX is cheaper than 5 props per chart.
//
// Both components stay agnostic about layout (no padding, no padding cleanup) —
// they render at the absolute positions the caller passes in.

import { DASH, INK, OPACITY, STROKE, TYPE } from "./tokens";

const AXIS_FONT = "ui-monospace, SFMono-Regular, Menlo, monospace";

export interface XAxisLabelsProps {
  /** Label position pairs: x is the center of the column in viewBox units,
   *  label is the rendered string (caller decides "7+" vs "7", "T0" vs "0"). */
  ticks: { x: number; label: string }[];
  /** Y position of the baseline (label baseline sits below this). */
  y: number;
  /** Optional vertical offset from baseline (default: 8). */
  offset?: number;
}

/** X-axis labels only — no tick marks, no axis line. Monospace, 60% black. */
export function XAxisLabels({ ticks, y, offset = 8 }: XAxisLabelsProps) {
  return (
    <g aria-hidden>
      {ticks.map((t) => (
        <text
          key={t.label + t.x}
          x={t.x}
          y={y + offset}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontFamily={AXIS_FONT}
          fontSize={TYPE.axisLabel}
          letterSpacing={TYPE.trackingTight}
          fill={INK.axisFill}
          fillOpacity={0.7}
        >
          {t.label}
        </text>
      ))}
    </g>
  );
}

export interface YGridLinesProps {
  /** Tick values (raw data values, not pixel positions). */
  values: number[];
  /** Function: data value → y pixel in viewBox units. */
  yScale: (v: number) => number;
  /** Left x of the grid (typically 0 or PAD_L). */
  x1: number;
  /** Right x of the grid. */
  x2: number;
  /** Whether to render labels at left edge of each gridline (default true). */
  showLabels?: boolean;
  /** X position for labels (defaults to x1 - 4). */
  labelX?: number;
  /** Format the label string (default: integer truncation). */
  formatLabel?: (v: number) => string;
  /** If a value should render as a solid baseline rule instead of dashed
   *  (typically y=0 for ManaCurve, y=20 for LifeChart). */
  baselineValue?: number;
}

/** Horizontal gridlines at the supplied tick values. Dashed, 8% black, no
 *  tick marks. Optionally renders the value at the left edge. */
export function YGridLines({
  values,
  yScale,
  x1,
  x2,
  showLabels = true,
  labelX,
  formatLabel = (v) => String(Math.round(v)),
  baselineValue,
}: YGridLinesProps) {
  const lx = labelX ?? x1 - 4;
  return (
    <g aria-hidden>
      {values.map((v) => {
        const y = yScale(v);
        const isBaseline = baselineValue !== undefined && v === baselineValue;
        return (
          <g key={v}>
            <line
              x1={x1}
              x2={x2}
              y1={y}
              y2={y}
              stroke="currentColor"
              strokeOpacity={isBaseline ? OPACITY.baseline : OPACITY.grid}
              strokeWidth={isBaseline ? STROKE.baseline : STROKE.grid}
              strokeDasharray={isBaseline ? undefined : DASH.grid}
            />
            {showLabels && (
              <text
                x={lx}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fontFamily={AXIS_FONT}
                fontSize={TYPE.axisLabel}
                letterSpacing={TYPE.trackingTight}
                fill={INK.axisFill}
                fillOpacity={0.6}
              >
                {formatLabel(v)}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}
